import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductCategory } from '../productCategoriesApi';
import CategoryTag from '../category/CategoryTag';
import { buildCategoryTree } from '../category/categoryHierarchy';

interface CategoryTagPickerProps {
  categories: ProductCategory[];
  value?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
}

interface ChoiceProps {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const Choice: React.FC<ChoiceProps> = ({ selected, disabled, onClick, children }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    className={selected ? 'product-card-category-choice is-selected' : 'product-card-category-choice'}
    onClick={onClick}
  >
    {children}
  </button>
);

/**
 * Category choice as the category's own coloured tags (category/CategoryTag,
 * colour chosen on the category card). Main categories come first; when the
 * chosen main category has subcategories, a second row offers them as an
 * optional refinement ("Bez podkategorii" keeps the main category). Both rows
 * act as radio groups so the picker works as an antd Form.Item control and
 * with the keyboard. The form value is the chosen category id either way.
 */
const CategoryTagPicker: React.FC<CategoryTagPickerProps> = ({
  categories, value, onChange, disabled, ariaLabel,
}) => {
  const { t } = useTranslation(['products']);
  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const selectedRoot = tree.find((root) => (
    root.id === value || root.children?.some((child) => child.id === value)
  ));
  const subcategories = selectedRoot?.children || [];

  return (
    <div className="product-card-category-picker">
      <div className="product-card-category-pick" role="radiogroup" aria-label={ariaLabel}>
        {tree.map((category) => (
          <Choice
            key={category.id}
            selected={category.id === selectedRoot?.id}
            disabled={disabled}
            onClick={() => onChange?.(category.id)}
          >
            <CategoryTag categoryId={category.id} name={category.name} color={category.color} size="small" />
          </Choice>
        ))}
      </div>
      {selectedRoot && subcategories.length > 0 && (
        <div className="product-card-subcategory">
          <span className="product-card-subcategory__label">{t('products:card.subcategoryLabel')}</span>
          <div
            className="product-card-category-pick"
            role="radiogroup"
            aria-label={t('products:card.subcategoryLabel')}
          >
            <Choice
              selected={value === selectedRoot.id}
              disabled={disabled}
              onClick={() => onChange?.(selectedRoot.id)}
            >
              <span className="product-card-subcategory__none">{t('products:card.subcategoryNone')}</span>
            </Choice>
            {subcategories.map((category) => (
              <Choice
                key={category.id}
                selected={category.id === value}
                disabled={disabled}
                onClick={() => onChange?.(category.id)}
              >
                <CategoryTag categoryId={category.id} name={category.name} color={category.color} size="small" />
              </Choice>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryTagPicker;
